import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge, type Tone } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading, Notice } from '@/components/ui/States'
import { ClientCard, DocumentTopBar, SaveState, projectLabel } from '@/features/documents/DocumentChrome'
import { NewDocument } from '@/features/documents/NewDocument'
import { formatDateShort } from '@/lib/dates'
import { pluralise } from '@/lib/format'
import { jobName } from '@/lib/jobs'
import { describeError } from '@/lib/supabase'
import { useContract, useContracts, useDeleteContract, useUpdateContract, type Contract } from './queries'

export const CONTRACT_STATUS: Record<string, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  sent: { label: 'Sent', tone: 'amber' },
  signed: { label: 'Signed', tone: 'sage' },
}

const clientName = (row: Contract) => (row.contact ? [row.contact.first_name, row.contact.last_name].filter(Boolean).join(' ') : 'No client')

/**
 * Documents › Contracts (29 Oct 2026): contracts written in the Studio, and
 * New contract. For now a contract is a title and its text, marked sent and
 * signed by hand; signing in the Studio and templates come later. Couples
 * still sign in Studio Ninja.
 */
export function ContractsPage() {
  const query = useContracts()
  const [params, setParams] = useSearchParams()
  const forJob = params.get('new')
  const [adding, setAdding] = useState(forJob !== null)

  return (
    <Page>
      <PageHeader
        eyebrow="Documents"
        title="Contracts"
        meta={query.data ? <span>{pluralise(query.data.length, 'contract')}</span> : null}
        actions={
          <Button variant="primary" onClick={() => setAdding(true)}>
            New contract
          </Button>
        }
      />
      {adding ? (
        <NewDocument
          kind="contract"
          jobId={forJob}
          onClose={() => {
            setAdding(false)
            if (forJob) setParams({}, { replace: true })
          }}
        />
      ) : null}
      <Section>
        {query.isPending ? (
          <Loading label="Loading contracts" />
        ) : query.isError ? (
          <ErrorState title="Contracts could not load" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <EmptyState title="No contracts yet" body="Click New contract to write one for a client. Until signing moves into the Studio, couples sign in Studio Ninja." />
        ) : (
          <div className="overflow-x-auto">
            <table className="ledger min-w-[40rem]">
              <thead>
                <tr>
                  <th className="label">Contract</th>
                  <th className="label">Client</th>
                  <th className="label">Project</th>
                  <th className="label">Status</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/contracts/${row.id}`} className="font-semibold underline-offset-4 hover:underline">
                        {row.title}
                      </Link>
                      <span className="block text-[length:var(--text-2xs)] text-taupe">{formatDateShort(row.created_at.slice(0, 10))}</span>
                    </td>
                    <td>{clientName(row)}</td>
                    <td className="text-taupe">{row.job ? `${jobName(row.job)} · ${formatDateShort(row.job.event_date)}` : 'No project'}</td>
                    <td>
                      <Badge tone={CONTRACT_STATUS[row.status]?.tone ?? 'neutral'}>{CONTRACT_STATUS[row.status]?.label ?? row.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </Page>
  )
}

/** One contract: its title and text (saved as she types while a draft), and where it stands. */
export function ContractPage() {
  const { contractId = '' } = useParams()
  const query = useContract(contractId)
  if (query.isPending) return <Loading label="Opening the contract" />
  if (query.isError || !query.data)
    return (
      <Page>
        <ErrorState title="That contract could not open" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
      </Page>
    )
  return <ContractEditor key={query.data.id} contract={query.data} />
}

function ContractEditor({ contract }: { contract: Contract }) {
  const update = useUpdateContract()
  const remove = useDeleteContract()
  const navigate = useNavigate()
  const draft = contract.status === 'draft'
  const [title, setTitle] = useState(contract.title)
  const [body, setBody] = useState(contract.body)
  const [touched, setTouched] = useState(false)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const latest = useRef({ title, body })
  latest.current = { title, body }

  useEffect(() => {
    if (!touched || !draft) return
    const timer = window.setTimeout(() => {
      const now = latest.current
      if (!now.title.trim()) return
      update.mutate({ id: contract.id, title: now.title.trim(), body: now.body }, { onSuccess: () => setSavedAt(new Date()) })
    }, 900)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, body, touched, draft])

  const status = CONTRACT_STATUS[contract.status] ?? { label: contract.status, tone: 'neutral' as const }

  return (
    <Page className="max-w-[64rem]">
      <DocumentTopBar
        back="/contracts"
        title={title || 'Contract'}
        status={<Badge tone={status.tone}>{status.label}</Badge>}
        project={projectLabel(contract.job)}
        projectTo={contract.job ? `/weddings/${contract.job.id}` : null}
        save={draft ? <SaveState saving={update.isPending} savedAt={savedAt} failed={update.isError} /> : null}
        actions={
          <>
            {contract.status === 'draft' ? (
              <Button
                variant="primary"
                disabled={!body.trim()}
                onClick={() => update.mutate({ id: contract.id, status: 'sent', sent_at: new Date().toISOString() }, { onSuccess: () => toast.success('Contract marked as sent') })}
              >
                Mark as sent
              </Button>
            ) : null}
            {contract.status === 'sent' ? (
              <Button
                variant="primary"
                onClick={() => update.mutate({ id: contract.id, status: 'signed', signed_at: new Date().toISOString() }, { onSuccess: () => toast.success('Contract marked as signed') })}
              >
                Mark as signed
              </Button>
            ) : null}
          </>
        }
      />

      {draft ? (
        <Notice className="mb-4" tone="amber" title="Draft">
          Write the contract here. Signing in the Studio comes with contract templates; for now send it yourself and mark it sent, then signed.
        </Notice>
      ) : (
        <Notice className="mb-4" tone={contract.status === 'signed' ? 'sage' : 'amber'} title={status.label}>
          {contract.status === 'signed' && contract.signed_at
            ? `Marked signed on ${formatDateShort(contract.signed_at.slice(0, 10))}.`
            : contract.sent_at
              ? `Marked sent on ${formatDateShort(contract.sent_at.slice(0, 10))}. Once sent, the text is fixed.`
              : null}
        </Notice>
      )}

      <Section>
        <ClientCard client={contract.contact} />
      </Section>

      <Section title="The contract">
        <div className="flex flex-col gap-4">
          <Field label="Title">
            <Input disabled={!draft} value={title} onChange={(e) => (setTouched(true), setTitle(e.target.value))} />
          </Field>
          <Field label="Text">
            <Textarea rows={22} disabled={!draft} value={body} onChange={(e) => (setTouched(true), setBody(e.target.value))} />
          </Field>
        </div>
      </Section>

      {draft ? (
        <Button
          size="sm"
          variant="danger"
          loading={remove.isPending}
          onClick={() => {
            if (window.confirm('Delete this draft contract?')) remove.mutate(contract.id, { onSuccess: () => navigate('/contracts') })
          }}
        >
          Delete this draft
        </Button>
      ) : null}
    </Page>
  )
}
